import { useState, type FormEvent, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useAuth, useMe } from "../auth.tsx";
import { api, useAction, useApi, type Resource } from "../api.ts";
import { fmtDate, fmtDateTime, fmtRelative, parseTime, plural } from "../format.ts";
import { Badge, Button, Card, CopyButton, EmptyState, ErrorText, Field, Icon, Input, LoadError, Loading, Mono, PageHeader, cn, useConfirm, useToast, type IconName } from "../ui/index.tsx";
import { CountUp, EASE, Ring, Springy, Stage, pop, rise, stagger, useCalm } from "./member/kit.tsx";
import type { Me, Referrals, SessionInfo } from "../../shared/types.ts";

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <motion.div variants={rise}>
      <dt className="kicker">{label}</dt>
      <dd className="mt-1.5 text-sm font-medium">{children}</dd>
    </motion.div>
  );
}

type CheckState = "ok" | "warn" | "pending";
interface Check { icon: IconName; label: string; detail: string; state: CheckState }

/** HWID and session posture rolled into one animated score. Pending checks are left out of the score. */
function SecurityCard({ me, sessions }: { me: Me; sessions: Resource<SessionInfo[]> }) {
  const calm = useCalm();
  const others = (sessions.data ?? []).filter((s) => !s.current).length;
  const checks: Check[] = [
    me.hwid
      ? { icon: "monitor", label: "Hardware ID bound", detail: "Your license is tied to one machine.", state: "ok" }
      : { icon: "monitor", label: "Hardware ID not bound", detail: "It binds on your first loader sign-in.", state: "warn" },
    me.hwid_locked
      ? { icon: "shield", label: "HWID locked", detail: "Only the bound machine can sign in.", state: "ok" }
      : { icon: "shield", label: "HWID unlocked", detail: me.hwid ? "A new machine can bind to this account." : "Locks once a machine is bound.", state: "warn" },
    !sessions.data
      ? { icon: "user", label: "Checking sessions", detail: "Counting signed-in devices.", state: "pending" }
      : others === 0
        ? { icon: "user", label: "No other sessions", detail: "This browser is the only one signed in.", state: "ok" }
        : { icon: "user", label: `${plural(others, "other session")} signed in`, detail: "Sign out any you don't recognise.", state: "warn" },
  ];
  const scored = checks.filter((c) => c.state !== "pending");
  const passed = scored.filter((c) => c.state === "ok").length;
  const score = scored.length ? Math.round((passed / scored.length) * 100) : 0;
  const tone = score >= 100 ? { c: "var(--color-ok)", t: "text-ok", label: "SECURED" } : score >= 60 ? { c: "var(--color-warn)", t: "text-warn", label: "ATTENTION" } : { c: "var(--color-bad)", t: "text-bad", label: "EXPOSED" };

  return (
    <Card hud className="relative overflow-hidden">
      <div className="pointer-events-none absolute -left-10 top-0 size-64 rounded-full blur-3xl" style={{ background: tone.c, opacity: 0.1 }} aria-hidden />
      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
        <div className="flex items-center gap-5">
          <Ring fraction={score / 100} size={116} stroke={7} color={tone.c} glow={score >= 100} delay={0.15}>
            <div className="text-center leading-none">
              <CountUp value={score} className="font-display text-3xl font-bold tabular-nums" />
              <span className="block font-mono text-[9px] uppercase tracking-widest text-faint">score</span>
            </div>
          </Ring>
          <div>
            <div className="kicker">// security state</div>
            <motion.div key={tone.label} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className={cn("mt-1 font-display text-2xl font-semibold tracking-wide", tone.t)}>{tone.label}</motion.div>
            <div className="mt-1 text-xs text-muted">
              <CountUp value={(sessions.data ?? []).length} /> active {(sessions.data ?? []).length === 1 ? "session" : "sessions"} · up to {plural(me.max_devices, "device")}
            </div>
          </div>
        </div>
        <motion.ul className="grid flex-1 gap-2 sm:pl-4" variants={stagger(0.12, 0.3)} initial="hidden" animate="show">
          {checks.map((c) => (
            <motion.li key={c.label} variants={rise} className="flex items-center gap-3 rounded-lg border border-line bg-bg/50 px-3 py-2">
              <motion.span variants={pop} className={cn("grid size-8 shrink-0 place-items-center rounded-md", c.state === "ok" ? "bg-ok/10 text-ok" : c.state === "warn" ? "bg-warn/10 text-warn" : "bg-raised text-faint")}>
                <Icon name={c.state === "ok" ? "check" : c.state === "warn" ? "alert" : c.icon} className={cn("size-4", c.state === "pending" && !calm && "animate-pulse-soft")} />
              </motion.span>
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">{c.label}</div>
                <div className="truncate text-xs text-muted">{c.detail}</div>
              </div>
            </motion.li>
          ))}
        </motion.ul>
      </div>
    </Card>
  );
}

function PasswordCard() {
  const toast = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [mismatch, setMismatch] = useState(false);

  const change = useAction(async () => {
    await api.post("/me/password", { current_password: current, new_password: next });
    setCurrent(""); setNext(""); setConfirm("");
    toast("Password updated", "success");
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (next !== confirm) { setMismatch(true); return; }
    setMismatch(false);
    void change.run();
  };

  return (
    <Card title="Change password">
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label="Current password"><Input type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>
        <Field label="New password" hint="8 to 128 characters"><Input type="password" autoComplete="new-password" required minLength={8} maxLength={128} value={next} onChange={(e) => setNext(e.target.value)} /></Field>
        <Field label="Confirm new password" error={mismatch ? "Passwords don't match" : null}><Input type="password" autoComplete="new-password" required minLength={8} maxLength={128} value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
        <ErrorText>{change.error}</ErrorText>
        <Springy><Button type="submit" variant="primary" loading={change.pending}>Update password</Button></Springy>
      </form>
    </Card>
  );
}

/** Optional recovery email. Reset links only go to a verified address, and every change needs the current password. */
function EmailCard({ me }: { me: Me }) {
  const { setMe, refresh } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const save = useAction(async () => {
    try {
      setMe(await api.put<Me>("/me/email", { email: email.trim(), current_password: password }));
    } catch (e) {
      // "Saved, but the mail failed" still changed the account, so pick up the new state before showing the error.
      await refresh().catch(() => {});
      throw e;
    }
    setEmail(""); setPassword("");
    toast("Verification link sent. Check your inbox.", "success");
  });
  const remove = useAction(async () => {
    if (!password) throw new Error("Enter your current password to remove your email");
    if (!(await confirm({ title: "Remove your email?", body: "You won't be able to reset your password by email until you add one again.", confirmLabel: "Remove", danger: true }))) return;
    setMe(await api.post<Me>("/me/email/remove", { current_password: password }));
    setPassword("");
    toast("Email removed", "success");
  });
  const resend = useAction(async () => {
    await api.post("/me/email/resend");
    toast("Verification link sent", "success");
  });

  const onSubmit = (e: FormEvent) => { e.preventDefault(); void save.run(); };

  return (
    <Card title="Email & recovery" description="Optional. A verified email lets you reset your password if you lose it.">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {me.email ? (
            <>
              <span className="break-all text-sm font-medium">{me.email}</span>
              {me.email_verified ? <Badge tone="ok" dot>Verified</Badge> : <Badge tone="warn" dot>Unverified</Badge>}
              {!me.email_verified && me.email_enabled && <Button size="sm" variant="ghost" loading={resend.pending} onClick={() => void resend.run()}>Resend link</Button>}
            </>
          ) : <span className="text-sm text-muted">No email on this account.</span>}
        </div>
        {me.email && !me.email_verified && me.email_enabled && <p className="text-xs text-muted">Click the link we sent to confirm it. Until then it can't be used to reset your password.</p>}
        {!me.email_enabled && <p className="text-sm text-muted">Email features aren't available on this server right now, so a recovery email can't be added or changed.</p>}
        {(me.email_enabled || me.email) && (
          <form onSubmit={onSubmit} className="space-y-4">
            {me.email_enabled && (
              <Field label={me.email ? "New email" : "Email"}>
                <Input type="email" autoComplete="email" required maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
            )}
            <Field label="Current password" hint="Needed for any email change.">
              <Input type="password" autoComplete="current-password" required={me.email_enabled} value={password} onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <ErrorText>{save.error ?? remove.error ?? resend.error}</ErrorText>
            <div className="flex flex-wrap items-center gap-3">
              {me.email_enabled && <Springy><Button type="submit" variant="primary" loading={save.pending}>{me.email ? "Change email" : "Add email"}</Button></Springy>}
              {me.email && <Button type="button" variant="danger" loading={remove.pending} onClick={() => void remove.run()}>Remove</Button>}
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}

function HwidCard({ me }: { me: Me }) {
  const { setMe } = useAuth();
  const confirm = useConfirm();
  const toast = useToast();
  const available = parseTime(me.hwid_reset_available_at);
  const cooling = !!available && available.getTime() > Date.now();

  const reset = useAction(async () => {
    const ok = await confirm({
      title: "Reset hardware ID?",
      body: "Your next loader sign-in will bind to the new machine. You can only do this once per cooldown period.",
      confirmLabel: "Reset HWID",
      danger: true,
    });
    if (!ok) return;
    setMe(await api.post<Me>("/me/hwid-reset"));
    toast("HWID reset", "success");
  });

  return (
    <Card title="Hardware ID" description="The machine your license is tied to.">
      <div className="flex flex-wrap items-center gap-2">
        {me.hwid ? <Mono className="break-all text-sm text-fg">{me.hwid}</Mono> : <span className="text-sm text-muted">Not bound yet. It binds on your first loader sign-in.</span>}
        {me.hwid && <CopyButton text={me.hwid} label="Copy HWID" />}
        {me.hwid && (
          <AnimatePresence mode="wait" initial={false}>
            <motion.span key={String(me.hwid_locked)} initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }} transition={{ duration: 0.18 }}>
              <Badge tone={me.hwid_locked ? "warn" : "neutral"}>{me.hwid_locked ? "Locked" : "Unlocked"}</Badge>
            </motion.span>
          </AnimatePresence>
        )}
      </div>
      {me.hwid_reset_at && <p className="mt-3 text-xs text-faint">Last reset {fmtRelative(me.hwid_reset_at)}.</p>}
      <div className="mt-4 space-y-3">
        <ErrorText>{reset.error}</ErrorText>
        <div className="flex flex-wrap items-center gap-3">
          <Springy><Button variant="danger" icon="refresh" loading={reset.pending} disabled={!me.hwid || cooling} onClick={() => void reset.run()}>Reset HWID</Button></Springy>
          {cooling && <span className="text-xs text-muted">Available again {fmtRelative(me.hwid_reset_available_at)} ({fmtDateTime(me.hwid_reset_available_at)}).</span>}
        </div>
      </div>
    </Card>
  );
}

function ReferralCard() {
  const referrals = useApi<Referrals>("/me/referrals");
  const calm = useCalm();
  const r = referrals.data;
  const link = r ? `${location.origin}/register?ref=${encodeURIComponent(r.code)}` : "";
  return (
    <Card title="Referrals" className="glow-border motion-reduce:before:[animation:none]"
      description={r ? `Each friend who signs up with your link earns you ${plural(r.bonus_days, "bonus day")}.` : undefined}>
      {referrals.loading && !r ? <Loading /> : referrals.error && !r ? <LoadError error={referrals.error} retry={referrals.reload} /> : r && (
        <div className="space-y-4">
          <div className="relative flex items-center gap-2 overflow-hidden rounded-lg border border-accent/30 bg-bg px-3 py-2">
            {!calm && (
              <motion.span className="pointer-events-none absolute inset-y-0 w-1/4 bg-gradient-to-r from-transparent via-accent/20 to-transparent" aria-hidden
                initial={{ x: "-120%" }} animate={{ x: "520%" }} transition={{ duration: 2.4, repeat: Infinity, repeatDelay: 1.6, ease: "easeInOut" }} />
            )}
            <Mono className="relative min-w-0 flex-1 truncate">{link}</Mono>
            <span className="relative"><CopyButton text={link} label="Copy referral link" /></span>
          </div>
          <div className="flex items-baseline gap-2">
            <CountUp value={r.referred.length} className="font-display text-3xl font-semibold tabular-nums" />
            <span className="text-xs text-muted">{r.referred.length === 1 ? "friend joined" : "friends joined"}</span>
          </div>
          {r.referred.length === 0 ? <p className="text-sm text-muted">Nobody has used your link yet.</p> : (
            <motion.ul className="divide-y divide-line rounded-lg border border-line" variants={stagger(0.06)} initial="hidden" animate="show">
              {r.referred.map((u) => (
                <motion.li key={`${u.username}-${u.created_at}`} variants={rise} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span>{u.username}</span>
                  <span className="text-xs text-faint">{fmtDate(u.created_at)}</span>
                </motion.li>
              ))}
            </motion.ul>
          )}
        </div>
      )}
    </Card>
  );
}

function SessionsCard({ sessions }: { sessions: Resource<SessionInfo[]> }) {
  const confirm = useConfirm();
  const toast = useToast();
  const list = sessions.data ?? [];

  const revoke = useAction(async (ids: string[]) => {
    await Promise.all(ids.map((id) => api.del(`/me/sessions/${encodeURIComponent(id)}`)));
    toast(ids.length > 1 ? "Sessions signed out" : "Session signed out", "success");
    await sessions.reload();
  });

  const others = list.filter((s) => !s.current);
  const revokeOthers = async () => {
    if (await confirm({ title: "Sign out other sessions?", body: `${plural(others.length, "other session")} will be signed out.`, confirmLabel: "Sign out", danger: true })) {
      await revoke.run(others.map((s) => s.id));
    }
  };
  const revokeOne = async (s: SessionInfo) => {
    if (await confirm({ title: "Sign out this session?", confirmLabel: "Sign out", danger: true })) await revoke.run([s.id]);
  };

  return (
    <Card title="Active sessions" padded={false} className="overflow-hidden"
      actions={others.length > 0 ? <Button size="sm" variant="danger" loading={revoke.pending} onClick={revokeOthers}>Sign out others</Button> : undefined}>
      {revoke.error && <div className="p-4"><ErrorText>{revoke.error}</ErrorText></div>}
      {sessions.loading && !sessions.data ? <Loading /> : sessions.error && !sessions.data ? <LoadError error={sessions.error} retry={sessions.reload} /> :
        list.length === 0 ? <EmptyState icon="monitor" title="No active sessions" /> : (
          <ul className="divide-y divide-line">
            <AnimatePresence initial={false} mode="popLayout">
              {list.map((s, i) => (
                <motion.li key={s.id} layout="position" initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 60, backgroundColor: "rgba(255,77,77,0.18)" }}
                  transition={{ type: "spring", stiffness: 340, damping: 30, delay: i < 6 ? i * 0.04 : 0 }}
                  className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3.5 text-sm transition-colors hover:bg-hover/40">
                  <span className="flex min-w-[9rem] items-center gap-2">
                    <Badge tone={s.kind === "loader" ? "accent" : "info"}>{s.kind}</Badge>
                    {s.current && <Badge tone="ok" dot>This session</Badge>}
                  </span>
                  <span className="min-w-[8rem]">{s.hwid ? <Mono>{s.hwid.slice(0, 12)}…</Mono> : <span className="text-faint">No HWID</span>}</span>
                  <span className="text-muted">{s.ip ?? "—"}{s.country ? ` · ${s.country}` : ""}</span>
                  <span className="whitespace-nowrap text-muted sm:ml-auto">{fmtRelative(s.last_seen_at)}</span>
                  <span className="w-20 text-right">{!s.current && <Button size="sm" variant="ghost" onClick={() => revokeOne(s)}>Revoke</Button>}</span>
                </motion.li>
              ))}
            </AnimatePresence>
          </ul>
        )}
    </Card>
  );
}

export const DiscordGlyph = ({ className = "size-5" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M20.3 4.4A19.6 19.6 0 0 0 15.4 3l-.6 1.3a18.2 18.2 0 0 0-5.6 0L8.6 3a19.5 19.5 0 0 0-4.9 1.4C.6 9 0 13.5.3 18a19.8 19.8 0 0 0 6 3l1.3-2.1c-.7-.3-1.4-.6-2-1l.5-.4a14 14 0 0 0 11.9 0l.5.4c-.6.4-1.3.7-2 1l1.3 2.1a19.7 19.7 0 0 0 6-3c.4-5.2-.7-9.6-3.5-13.6ZM8.5 15.2c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4Zm7 0c-1.2 0-2.1-1.1-2.1-2.4s.9-2.4 2.1-2.4 2.2 1.1 2.1 2.4c0 1.3-.9 2.4-2.1 2.4Z" />
  </svg>
);

function DiscordCard({ me }: { me: Me }) {
  const { setMe } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const unlink = useAction(async () => {
    if (!(await confirm({ title: "Unlink Discord?", body: "Your game roles in the ExoWare Discord will be removed.", confirmLabel: "Unlink", danger: true }))) return;
    setMe(await api.del<Me>("/me/discord"));
    toast("Discord unlinked", "success");
  });
  return (
    <Card title="Discord" description="Linked accounts get a Customer role and a role for each game they own.">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={cn("grid size-11 place-items-center rounded-xl", me.discord ? "bg-[#5865F2] text-white shadow-[0_0_24px_-6px_#5865F2]" : "bg-raised text-muted")}><DiscordGlyph /></div>
          <div>
            <div className="font-medium">{me.discord ? me.discord.name : "Not linked"}</div>
            <div className="text-sm text-muted">{me.discord ? "Roles sync automatically when your licenses change." : "Join the ExoWare Discord and run /link to connect."}</div>
          </div>
        </div>
        {me.discord && <Button variant="danger" size="sm" loading={unlink.pending} onClick={() => void unlink.run()}>Unlink</Button>}
      </div>
      <ErrorText>{unlink.error}</ErrorText>
    </Card>
  );
}

export default function Account() {
  const me = useMe();
  const sessions = useApi<SessionInfo[]>("/me/sessions");
  const block = (i: number) => ({ initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, delay: 0.06 + i * 0.09, ease: EASE } });
  return (
    <Stage>
      <PageHeader title="Account" description="Your profile, security and devices." />
      <div className="space-y-6">
        <motion.div {...block(0)}><SecurityCard me={me} sessions={sessions} /></motion.div>
        <motion.div {...block(1)}>
          <Card title="Profile">
            <motion.dl className="grid gap-5 sm:grid-cols-4" variants={stagger(0.08, 0.2)} initial="hidden" animate="show">
              <Fact label="Username">{me.username}</Fact>
              <Fact label="Member since">{fmtDate(me.created_at)}</Fact>
              <Fact label="Country">{me.country ?? "—"}</Fact>
              <Fact label="Operating system">{me.os_version ?? "—"}</Fact>
            </motion.dl>
          </Card>
        </motion.div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <motion.div {...block(2)} className="space-y-6"><PasswordCard /><EmailCard me={me} /></motion.div>
          <motion.div {...block(3)} className="space-y-6">
            <HwidCard me={me} />
            <DiscordCard me={me} />
            <ReferralCard />
          </motion.div>
        </div>
        <motion.div {...block(4)}><SessionsCard sessions={sessions} /></motion.div>
      </div>
    </Stage>
  );
}
